import { Avatar } from "@admin/components/atoms/Avatar";
import { Badge } from "@admin/components/atoms/Badge";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import {
  CompanyLocation,
  dateToISO,
  getDateRange,
  getStaffingExpense,
  Periods,
  StaffShiftAnalysis,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { TableHeader } from "./TableHeader";

export interface PdfData {
  dateRange: DateRange;
  staffShift: StaffShiftAnalysis[];
  locationName: string;
}

interface ReportsTableProps {
  selectedLocation?: CompanyLocation;
  companyId: string;
}

export function ReportsTable({
  selectedLocation,
  companyId,
}: ReportsTableProps) {
  const { t, i18n } = useTranslation();
  const [selectedWageTypes, setSelectedWageTypes] = useState<number[]>([1, 3]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: getDateRange(Periods.ThisMonth, true).start,
    to: getDateRange(Periods.ThisMonth, true).end,
  });

  const [totalInformations, setTotalInformations] = useState({
    totalHours: 0,
    totalWage: 0,
  });

  const params = useMemo(() => {
    return {
      companyId,
      locationId: selectedLocation?.id ?? "",
      startDate: dateRange?.from ? dateToISO(dateRange.from) : "",
      endDate: dateRange?.to ? dateToISO(dateRange.to) : "",
    };
  }, [companyId, selectedLocation?.id, dateRange]);

  const { data: staffShift, isPending } = useQuery({
    queryKey: ["staffingExpense", params],
    queryFn: () => getStaffingExpense(params),
    enabled: !!selectedLocation && !!dateRange,
  });

  useEffect(() => {
    if (staffShift && dateRange) {
      let totalHours = 0;
      let totalWage = 0;
      staffShift
        .filter((shift) => selectedWageTypes.includes(shift.wageType))
        .forEach((shift) => {
          totalHours += shift.totalWorkingHours;
          totalWage += shift.fee;
        });
      setTotalInformations({ totalHours, totalWage });
    }
  }, [staffShift, selectedWageTypes, dateRange]);

  const handleDateRangeChange = (dr: DateRange) => {
    setDateRange(dr);
  };

  const tableData = useMemo(() => {
    if (!staffShift) return [];
    return staffShift
      .filter((shift) => selectedWageTypes.includes(shift.wageType))
      .map((shift) => ({ ...shift }));
  }, [staffShift, selectedWageTypes]);

  const columns: DataTableColumn<StaffShiftAnalysis>[] = [
    {
      header: t("common.Personnel"),
      enableSorting: true,
      accessorKey: "personnel",
      sortingFn: (rowA, rowB) => {
        return (rowA.original.firstName + rowA.original.lastName).localeCompare(
          rowB.original.firstName + rowB.original.lastName,
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex flex-row gap-2.5 items-center">
            <Avatar
              alt={`${row.original.firstName} ${row.original.lastName}`}
              className="rounded-full size-9 shrink-0 object-cover"
            />
            <div>
              <p className="text-gray-800 font-normal">
                {row.original.firstName} {row.original.lastName}
              </p>
              <p className="text-gray-700 font-normal text-[13px]">
                @{row.original.lastName}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      header: t("shift.Wage Type and Amount"),
      align: "center",
      accessorKey: "wageType",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return rowA.original.wageType - rowB.original.wageType;
      },
      cell: ({ row }) => {
        const { wageType } = row.original;
        const wageLabel =
          wageType === 1
            ? `${t("personnel.Hourly")} / ${row.original.wageAmount} DKK`
            : `${t("personnel.Monthly")} / ${row.original.wageAmount} DKK`;
        return <div className="text-center">{wageLabel}</div>;
      },
    },
    {
      header: t("shift.Total Working Time"),
      align: "center",
      accessorKey: "totalWorkingHours",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.totalWorkingHours - rowB.original.totalWorkingHours
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          <span>{row.original.totalWorkingHours.toFixed(2)} h</span>
        </div>
      ),
    },
    {
      header: t("shift.Wage"),
      align: "center",
      accessorKey: "actualwage",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        return rowA.original.fee - rowB.original.fee;
      },
      cell: ({ row }) => (
        <div className="text-center">
          <span>{row.original.fee.toFixed(2)} DKK</span>
        </div>
      ),
    },
    {
      header: t("shift.Warnings and Explanations"),
      align: "center",
      enableSorting: true,
      accessorKey: "warnings",
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.totalWorkingHours > 160 ? (
            <Badge type="danger" size="md">
              {t("shift.Exceeded 198h Limit")}
            </Badge>
          ) : (
            <Badge type="success" size="md">
              {t("shift.No Warnings")}
            </Badge>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="card card-grid min-w-full">
      <TableHeader
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        setSelectedWageTypes={setSelectedWageTypes}
        pdfData={
          {
            dateRange: dateRange,
            staffShift: staffShift,
            locationName: selectedLocation?.name,
          } as PdfData
        }
      />
      <DataTable
        loading={isPending}
        columns={columns}
        data={tableData}
        sorting={[{ id: "warnings", desc: false }]}
        isInfoIcon={false}
        enablePagination={false}
        title={`${t("shift.Selected Time Period")}: ${Intl.DateTimeFormat(
          i18n.language,
          {
            month: "short",
            day: "numeric",
            year: "numeric",
          },
        ).format(
          new Date(dateRange?.from ?? new Date()),
        )} - ${Intl.DateTimeFormat(i18n.language, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(new Date(dateRange?.to ?? new Date()))}`}
        footer={
          <tr className="bg-gray-50">
            <td className="text-center font-semibold">{t("shift.Totals")}</td>
            <td>-</td>
            <td className="text-center font-semibold">
              {totalInformations.totalHours.toFixed(2)} h
            </td>
            <td className="text-center font-semibold">
              {totalInformations.totalWage.toFixed(2)} DKK
            </td>
            <td></td>
          </tr>
        }
      />
    </div>
  );
}
